import { z } from 'zod';
import {
  dataHoraSchema,
  listagemDeCadastroSchema,
  respostaListaSchema,
  uuidSchema,
} from './comum.ts';

/**
 * Conversão entre unidades de medida (`.spec/015`, decisão 20).
 *
 * Fatores **sugeridos** entre unidades de **mesma dimensão** — KG para G, T para
 * KG, milheiro para peça. É o que evita redigitar `1000` em cada KG→G de cada
 * fornecedor, que é exatamente a digitação que produz `100` e `10000`; e um fator
 * errado por um fator de dez no estoque não se percebe olhando a tela.
 *
 * **Só sugere.** Quem vale é o fator da linha de `fornecedores_itens`, e a
 * divergência entre os dois é comportamento correto: quem fatura em "KG" mas
 * entrega saco de 25 kg tem fator próprio, e a sugestão não pode vencer o que
 * alguém observou na entrega. É a mesma relação que a operação fiscal tem com a
 * conta contábil (`.spec/015`, decisão 17).
 *
 * **O cadastro não cobre par de dimensão diferente** — KG↔M², M³↔UN. Ali não
 * existe fator do par: chapa de 2 mm dá 15,7 kg/m² e de 3 mm dá 23,55, e só a
 * linha que conhece o produto sabe. Não há validação de dimensão porque o
 * cadastro de unidade não guarda dimensão nenhuma; o que existe é a tela
 * explicando, e a liberdade de cadastrar um par que só faz sentido para quem o
 * cadastrou.
 */

/* -------------------------------------------------------------------------- */
/* O fator                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * As duas metades do fator, **estritamente positivas**.
 *
 * Seis casas e doze dígitos inteiros, que é o que `numeric(18,6)` guarda — mas
 * com regex próprio em vez de `precoUnitarioSchema`, porque aqui zero e negativo
 * não são só improváveis: **zero no denominador é divisão por zero** na
 * conversão, e negativo inverteria o sentido da quantidade sem que nada acusasse.
 *
 * O `(?=.*[1-9])` é "tem algum dígito diferente de zero em algum lugar", e é o
 * que separa `0.5` (válido) de `0`, `0.0` e `0.000000` (recusados). A checagem é
 * textual e não converte para número: a decisão de trafegar decimal como texto
 * (ADR 0003) vale aqui como em qualquer outra grandeza.
 */
export const fatorConversaoSchema = z
  .string()
  .regex(
    /^(?=.*[1-9])\d{1,12}(\.\d{1,6})?$/,
    'Fator inválido: use ponto, até 6 casas, e um valor maior que zero.',
  );

/* -------------------------------------------------------------------------- */
/* Cadastro                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * O payload.
 *
 * As duas metades do fator vêm **separadas, e não como um número só**. É a
 * decisão 20: "20 SC = 1.000 KG" grava `1000/20`, e 1 m³ de peça 3,00 × 0,15 ×
 * 0,05 grava `1000000/22500` em vez de `44,444444` — o decimal perderia a dízima
 * para dentro do custo de estoque, onde ninguém procura. E o par diz **de onde o
 * fator veio**, que é a diferença entre auditar e adivinhar.
 */
export const criarConversaoUnidadeMedidaSchema = z.object({
  unidadeOrigemId: uuidSchema,
  unidadeDestinoId: uuidSchema,
  /** Quantas unidades de **destino** cabem em `fatorDenominador` de origem. */
  fatorNumerador: fatorConversaoSchema,
  fatorDenominador: fatorConversaoSchema,
  ativo: z.boolean().prefault(true),
});

export type CriarConversaoUnidadeMedida = z.infer<typeof criarConversaoUnidadeMedidaSchema>;
export const atualizarConversaoUnidadeMedidaSchema = criarConversaoUnidadeMedidaSchema;
export type AtualizarConversaoUnidadeMedida = z.infer<typeof atualizarConversaoUnidadeMedidaSchema>;

/**
 * A leitura traz as **siglas** das duas unidades, resolvidas por junção.
 *
 * Sem elas a listagem precisaria de uma consulta por linha para exibir "SC → KG",
 * que é a única forma legível de ler esta tabela: um par de UUIDs não diz nada a
 * ninguém.
 */
export const conversaoUnidadeMedidaSchema = z.object({
  id: uuidSchema,
  unidadeOrigemId: uuidSchema,
  unidadeOrigemSigla: z.string(),
  unidadeDestinoId: uuidSchema,
  unidadeDestinoSigla: z.string(),
  fatorNumerador: z.string(),
  fatorDenominador: z.string(),
  ativo: z.boolean(),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type ConversaoUnidadeMedida = z.infer<typeof conversaoUnidadeMedidaSchema>;

export const listaConversoesUnidadeMedidaSchema = respostaListaSchema(conversaoUnidadeMedidaSchema);
export type ListaConversoesUnidadeMedida = z.infer<typeof listaConversoesUnidadeMedidaSchema>;

/**
 * Listagem, com os dois filtros que o de‑para do fornecedor faz.
 *
 * `unidadeOrigemId` + `unidadeDestinoId` é a pergunta "qual fator sugerir para
 * este par?", que é o uso principal desta tabela — a tela de conferência do XML a
 * consulta ao montar a linha. Os dois são independentes porque o cadastro também
 * responde "o que já converte a partir de KG?".
 */
export const listarConversoesUnidadeMedidaQuerySchema = listagemDeCadastroSchema.extend({
  unidadeOrigemId: uuidSchema.optional(),
  unidadeDestinoId: uuidSchema.optional(),
});

export type ListarConversoesUnidadeMedidaQuery = z.infer<
  typeof listarConversoesUnidadeMedidaQuerySchema
>;

/**
 * Colunas de ordenação.
 *
 * A queda é a **sigla da origem**: é como o cadastro se lê, agrupado por "a
 * partir de que unidade". Por data de criação a lista sairia na ordem em que
 * alguém digitou, que não é ordem nenhuma para quem procura um par.
 */
export const COLUNAS_ORDENAVEIS_CONVERSAO_UNIDADE = [
  'unidadeOrigemSigla',
  'unidadeDestinoSigla',
  'createdAt',
] as const;

/* -------------------------------------------------------------------------- */
/* O par não pode ter as duas pontas iguais                                   */
/* -------------------------------------------------------------------------- */

/**
 * As duas unidades do par são diferentes?
 *
 * Mora no contrato porque a mesma pergunta é feita na tela — para o campo acusar
 * antes do envio — e no service, que é a fronteira que vale para um script de
 * importação. Duas implementações divergiriam, e a divergência apareceria como
 * formulário que deixa salvar e servidor que recusa.
 *
 * KG→KG não é conversão: é a identidade, e ela já é o padrão `1/1` de quem não
 * declara conversão nenhuma. Cadastrá-la criaria uma linha que a leitura teria de
 * aprender a ignorar.
 */
export function parDeUnidadesValido(entrada: {
  unidadeOrigemId: string;
  unidadeDestinoId: string;
}): boolean {
  return entrada.unidadeOrigemId !== entrada.unidadeDestinoId;
}
